/**
 * Tenant Compliance Repository Implementation
 * Implements the TenantComplianceRepository interface
 * Coordinates between API calls and mappers
 */

import { TenantComplianceRepository } from "../../domain/repositories/TenantComplianceRepository";
import {
  ComplianceTypeEntity,
  EnabledComplianceTypeEntity,
  MasterCalendarEntity,
  GroupedCalendarEntries,
  SyncStatusEntity,
  TenantComplianceEntity,
  ComplianceProofEntity,
} from "../../domain/entities";
import {
  getMasterTypes,
  getEnabledTypes,
  enableType,
  disableType,
  getCalendarGrouped,
  getSyncStatus,
  manualSync,
  getCompliances,
  getCompliance,
  uploadProof,
  submitForReview,
  deleteProof,
} from "../api/tenantComplianceApi";
import { ComplianceMapper } from "../mappers/ComplianceMapper";

export class TenantComplianceRepositoryImpl
  implements TenantComplianceRepository
{
  // Master Compliance Types (Read-only)
  async getMasterTypes(): Promise<ComplianceTypeEntity[]> {
    const response = await getMasterTypes();
    if (response.success && response.data) {
      return ComplianceMapper.toComplianceTypeEntityArray(response.data);
    }
    throw new Error("Failed to fetch master compliance types");
  }

  // Enabled Compliance Types
  async getEnabledTypes(): Promise<EnabledComplianceTypeEntity[]> {
    const response = await getEnabledTypes();
    if (response.success && response.data) {
      return ComplianceMapper.toEnabledComplianceTypeEntityArray(response.data);
    }
    throw new Error("Failed to fetch enabled compliance types");
  }

  async enableType(
    type: ComplianceTypeEntity
  ): Promise<EnabledComplianceTypeEntity> {
    // Send full type object to backend (no API call needed - data already fetched)
    const response = await enableType({
      id: type.id, // master's compliance_types.id
      compliance_type: type.compliance_type,
      compliance_name: type.compliance_name,
      authority: type.authority,
      frequency: type.frequency,
      description: type.description,
      is_active: type.is_active ? 1 : 0, // Convert boolean to 0/1
    });
    if (response.success && response.data) {
      return ComplianceMapper.toEnabledComplianceTypeEntity(response.data);
    }
    throw new Error("Failed to enable compliance type");
  }

  async disableType(id: number): Promise<void> {
    await disableType(id);
  }

  // Calendar View (Read-only)
  async getCalendarGrouped(): Promise<GroupedCalendarEntries> {
    const response = await getCalendarGrouped();
    if (response.success && response.data) {
      return ComplianceMapper.toGroupedCalendarEntries(response);
    }
    throw new Error("Failed to fetch grouped calendar entries");
  }

  // Sync Operations
  async getSyncStatus(): Promise<SyncStatusEntity> {
    const response = await getSyncStatus();
    if (response.success && response.data) {
      return ComplianceMapper.toSyncStatusEntity(response.data);
    }
    throw new Error("Failed to fetch sync status");
  }

  async manualSync(): Promise<{
    success: boolean;
    message: string;
    data: any;
  }> {
    const response = await manualSync();
    if (response.success) {
      return {
        success: response.success,
        message: response.message,
        data: response.data,
      };
    }
    throw new Error(response.message || "Failed to sync compliance calendar");
  }

  // Tenant Dashboard
  async getCompliances(filters?: {
    status?: string;
    period?: string;
    overdue?: boolean;
    search?: string;
    date_filter?: string;
    limit?: number;
    offset?: number;
  }): Promise<TenantComplianceEntity[]> {
    const response = await getCompliances(filters);
    if (response.success && response.data) {
      return ComplianceMapper.toTenantComplianceEntityArray(response.data);
    }
    throw new Error("Failed to fetch compliances");
  }

  async getCompliance(
    id: number
  ): Promise<
    TenantComplianceEntity & {
      proofs: ComplianceProofEntity[];
      compliance_type_details?: any;
    }
  > {
    const response = await getCompliance(id);
    if (response.success && response.data) {
      const compliance = ComplianceMapper.toTenantComplianceEntity(
        response.data
      );
      const proofs = response.data.proofs
        ? ComplianceMapper.toComplianceProofEntityArray(response.data.proofs)
        : [];
      return {
        ...compliance,
        proofs,
        compliance_type_details: response.data.compliance_type_details,
      };
    }
    throw new Error("Failed to fetch compliance details");
  }

  async uploadProof(
    complianceId: number,
    file: File,
    proofType: string = "screenshot",
    description?: string,
    isPrimary: boolean = false
  ): Promise<ComplianceProofEntity> {
    const response = await uploadProof(
      complianceId,
      file,
      proofType,
      description,
      isPrimary
    );
    if (response.success && response.data) {
      return ComplianceMapper.toComplianceProofEntity(response.data);
    }
    throw new Error("Failed to upload proof");
  }

  async submitForReview(
    complianceId: number,
    requestComments?: string
  ): Promise<void> {
    const response = await submitForReview(complianceId, requestComments);
    if (!response.success) {
      throw new Error(response.message || "Failed to submit for review");
    }
  }

  async deleteProof(proofId: number): Promise<void> {
    const response = await deleteProof(proofId);
    if (!response.success) {
      throw new Error(response.message || "Failed to delete proof");
    }
  }
}
